import mParticlem, { type MParticleWebSDK } from '@mparticle/web-sdk';
import type { User } from 'mparticle__web-sdk';

import { log } from '@/services/appMonitoringSuite';
import { fetchAPI } from '@/services/fetchAPI';
import { getSystem } from '@/system';
import { getLocalization } from '@/utils/localization';

const CDN_LOCATION = process.env.NEXT_PUBLIC_ONETRUST_CDN || '';
const DEVICE_TYPE = process.env.NEXT_PUBLIC_ONETRUST_DEVICE || '';
const APP_ID = process.env.NEXT_PUBLIC_ONETRUST_APP_ID || '';
const VERSION_NUMBER = process.env.NEXT_PUBLIC_ONETRUST_VERSION || '';

// temporary fix, because mParticle is not typed correctly on version 2.64.1 of the mParticle web SDK
const mParticle = mParticlem as MParticleWebSDK;

// https://developer.onetrust.com/onetrust/docs/get-banner-ui
export const oneTrustGetBannerUI = async (language: string) => {
  try {
    const consentString = oneTrustGetConsentString();

    const response = await fetchAPI(`${process.env.NEXT_PUBLIC_ONETRUST_URL}banner`, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'OT-CDN-Location': CDN_LOCATION,
        'OT-App-Id': APP_ID,
        'OT-Device-Type': DEVICE_TYPE,
        'OT-SDK-Version': VERSION_NUMBER,
        'OT-Language': language,
        ...(consentString && {
          'OT-Consent-String': consentString,
        }),
      },
    });

    const data = await response.json();

    if (data.otConsentString) {
      oneTrustSetConsentString(data.otConsentString);
    }

    return data;
  } catch (error) {
    console.error(`Failed to get banner UI`);
  }
};

// https://developer.onetrust.com/onetrust/docs/get-preference-center-ui
export const oneTrustGetPreferenceUI = async (language: string) => {
  try {
    const consentString = oneTrustGetConsentString();

    const response = await fetchAPI(
      `${process.env.NEXT_PUBLIC_ONETRUST_URL}preferences`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'OT-CDN-Location': CDN_LOCATION,
          'OT-App-Id': APP_ID,
          'OT-Device-Type': DEVICE_TYPE,
          'OT-SDK-Version': VERSION_NUMBER,
          'OT-Language': language,
          ...(consentString && {
            'OT-Consent-String': consentString,
          }),
        },
      }
    );

    const data = await response.json();

    if (data.otConsentString) {
      oneTrustSetConsentString(data.otConsentString);
    }
    return data;
  } catch (error) {
    console.error(`Failed to get Preference UI`);
  }
};

// https://developer.onetrust.com/onetrust/docs/save-and-log-consent
export const oneTrustLogConsent = async (
  language: string,
  interactionType: string,
  consent: any = null
) => {
  try {
    const consentString = oneTrustGetConsentString();

    const body = {
      interactionType,
      ...(consent && { consent }),
      userAgent: window?.navigator?.userAgent || '',
    };

    const response = await fetchAPI(
      `${process.env.NEXT_PUBLIC_ONETRUST_URL}save-log-consent`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'OT-CDN-Location': CDN_LOCATION,
          'OT-App-Id': APP_ID,
          'OT-Device-Type': DEVICE_TYPE,
          'OT-SDK-Version': VERSION_NUMBER,
          'OT-Language': language,
          ...(consentString && {
            'OT-Consent-String': consentString,
          }),
        },
        body: JSON.stringify(body),
      }
    );

    const data = await response.json();
    if (data.otConsentString) {
      oneTrustSetConsentString(data.otConsentString);
    }

    await mParticleTrackConsent(data?.storageKeys?.OT_GroupConsents);

    return data;
  } catch (error) {
    console.error(`Failed to log consent`);
    log(`OneTrust: failed to log consent (${interactionType})`, 'error');
    return null;
  }
};

export const oneTrustUserPreferences = async (language: string) => {
  try {
    const preferences = await oneTrustGetPreferenceUI(language);
    if (preferences) {
      const personalization =
        preferences?.pcUIData?.purposeTree?.purposes.find(
          (p: any) =>
            p.groupName ===
            getLocalization(language)['onetrust-preferences'].personalization
        )?.consentStatus ?? false;

      const analytics =
        preferences?.pcUIData?.purposeTree?.purposes.find(
          (p: any) =>
            p.groupName === getLocalization(language)['onetrust-preferences'].analytics
        )?.consentStatus ?? false;

      const advertising =
        preferences?.pcUIData?.purposeTree?.purposes.find(
          (p: any) =>
            p.groupName === getLocalization(language)['onetrust-preferences'].advertising
        )?.consentStatus ?? false;

      const geolocation =
        preferences?.pcUIData?.purposeTree?.purposes.find(
          (p: any) =>
            p.groupName === getLocalization(language)['onetrust-preferences'].geolocation
        )?.consentStatus ?? false;

      return {
        personalization: Boolean(personalization),
        analytics: Boolean(analytics),
        advertising: Boolean(advertising),
        geolocation: Boolean(geolocation),
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const CONSENT_LOCALSTORAGE_KEY = 'OT-Consent-String';

const oneTrustGetConsentString = () => {
  return localStorage.getItem(CONSENT_LOCALSTORAGE_KEY);
};

const oneTrustSetConsentString = (consent: string) => {
  localStorage.setItem(CONSENT_LOCALSTORAGE_KEY, consent);
};

enum OneTrustConsentLabels {
  C0007 = 'C0007',
  C0008 = 'C0008',
  C0009 = 'C0009',
  C0010 = 'C0010',
  C0011 = 'C0011',
}

enum mParticleConsentLabels {
  functional = 'functional',
  personalization = 'personalization',
  analytics = 'analytics',
  advertising = 'advertising',
  geolocation = 'geolocation',
}

const mParticleOneTrustLabelMap: Record<OneTrustConsentLabels, mParticleConsentLabels> = {
  [OneTrustConsentLabels.C0007]: mParticleConsentLabels.functional,
  [OneTrustConsentLabels.C0008]: mParticleConsentLabels.personalization,
  [OneTrustConsentLabels.C0009]: mParticleConsentLabels.analytics,
  [OneTrustConsentLabels.C0010]: mParticleConsentLabels.advertising,
  [OneTrustConsentLabels.C0011]: mParticleConsentLabels.geolocation,
};

const mParticleTrackConsent = async (consent: any) => {
  if (!consent) return;

  const user = mParticle.Identity.getCurrentUser?.() as User;
  const timestamp = Date.now();
  const deviceId = await getSystem().getDeviceId();
  const location = window.location.href;

  const consentState = mParticle.Consent.createConsentState();

  for (const groupId in consent) {
    const status = Boolean(consent[groupId]);
    const label = mParticleOneTrustLabelMap[groupId as OneTrustConsentLabels];

    consentState.addGDPRConsentState(
      label,
      mParticle.Consent.createGDPRConsent(status, timestamp, label, location, deviceId)
    );
  }

  user?.setConsentState(consentState);
};

export type BannerUIData = {
  appConfig: {
    showBanner: boolean;
  };
  bannerUIData: {
    general: {
      backgroundColor: string;
      backgroundColorDark: string;
      additionalDescriptionPlacement: string;
      useRTL: boolean;
      layout: string;
      buttonFocusColor: string;
      buttonFocusTextColor: string;
      buttonBorderShow: boolean;
      qrCodeAriaLabel: string;
      headingAriaLabel: string;
      selectedAriaLabel: string;
    };
    logo: {
      url: string;
      logoVoiceOverText: string;
    };
    summary: {
      title: {
        fontSize: string;
        text: string;
        textAlign: string;
        textColor: string;
        textColorDark: string;
      };
      description: {
        fontSize: string;
        text: string;
        textAlign: string;
        textColor: string;
        textColorDark: string;
      };
    };
    buttons: {
      acceptAll: {
        borderRadius: string;
        color: string;
        colorDark: string;
        fontSize: string;
        text: string;
        textColor: string;
        textColorDark: string;
        position: number;
        interactionType: string;
      };
      showPreferences: {
        borderRadius: string;
        color: string;
        colorDark: string;
        fontSize: string;
        showAsLink: boolean;
        text: string;
        textColor: string;
        textColorDark: string;
        position: number;
      };
    };
    links: {
      policyLink: {
        show: boolean;
        text: string;
        textAlign: string;
        fontSize: string;
        textColor: string;
        textColorDark: string;
        url: string;
        urlQRCode: string;
        linkVoiceOverText: string;
      };
    };
  };
};

export type PreferencesUIData = {
  pcUIData: {
    general: {
      backgroundColor: string;
      backgroundColorDark: string;
      sdkListShow: boolean;
      sdkListManageServicesLabel: string;
      sdkListLabel: string;
      sdkLevelOptOutShow: boolean;
      buttonFocusColor: string;
      buttonFocusTextColor: string;
      buttonBorderShow: boolean;
      useRTL: boolean;
      consentText: string;
      legitInterestText: string;
      illustrationsTitleText: string;
      allowAllConsentText: string;
      regionAriaLabel: string;
      listAriaLabel: string;
      sdkListTitleAriaLabel: string;
      filterSDKListAriaLabel: string;
      alphabeticFilterAriaLabel: string;
      copyConfirmationAriaLabel: string;
      clearButtonAnnouncement: string;
      qrCodeAriaLabel: string;
      headingAriaLabel: string;
      selectedAriaLabel: string;
      activeAriaLabel: string;
      filterSDKListTitle: string;
    };
    summary: {
      title: {
        fontSize: string;
        text: string;
        textAlign: string;
        textColor: string;
        textColorDark: string;
      };
      description: {
        fontSize: string;
        text: string;
        textAlign: string;
        textColor: string;
        textColorDark: string;
      };
    };
    buttons: {
      closeButton: {
        imgcolor: string;
        imgcolorDark: string;
        showText: boolean;
        closeBtnVoiceOverText: string;
        interactionType: string;
      };
      savePreferencesButton: {
        borderRadius: string;
        color: string;
        colorDark: string;
        fontSize: string;
        text: string;
        textColor: string;
        textColorDark: string;
        interactionType: string;
        position: number;
        homeIconShow: boolean;
      };
      backButton: {
        color: string;
        colorDark: string;
        backBtnVoiceOverText: string;
      };
    };
    links: {
      policyLink: {
        fontSize: string;
        text: string;
        textAlign: string;
        textColor: string;
        textColorDark: string;
        url: string;
        urlQRCode: string;
        linkVoiceOverText: string;
      };
    };
    purposeTree: {
      styling: {
        headerTitle: {
          show: boolean;
          text: string;
          fontSize: string;
          textAlign: string;
          textColor: string;
          textColorDark: string;
        };
        alwaysActiveLabel: {
          text: string;
          color: string;
          colorDark: string;
          fontSize: string;
        };
        itemTitle: {
          fontSize: string;
          textAlign: string;
          textColor: string;
          textColorDark: string;
        };
        itemDescription: {
          fontSize: string;
          textAlign: string;
          textColor: string;
          textColorDark: string;
        };
        itemDetailsLinks: {
          position: string;
          sdkListText: string;
          fullLegalText: string;
          descriptionDetails: string;
          iabLegalTextUrl: string;
        };
        itemDetailsConsentCheckboxInfo: {
          subCategoryHeaderText: string;
          interactionChoiceText: string;
          activeText: string;
          inActiveText: string;
        };
      };
      purposes: {
        showSDKListLink: boolean;
        parent: string;
        alwaysShowCategory: boolean;
        groupDescription: string;
        groupName: string;
        isIabPurpose: boolean;
        firstPartyCookies: {
          name: string;
          description: string;
          sdkId: string;
          toggleStatus: number;
        }[];
        purposeId: string;
        groupId: string;
        type: string;
        descriptionLegal: string;
        iabIllustrations: [];
        children: [];
        consentToggleStatus: number;
        consentStatus: number;
      }[];
    };
    filter: {
      onColor: string;
      onColorDark: string;
      color: string;
      colorDark: string;
      filterVoiceOverTextsSDKs: string;
      filterApplyText: string;
      filterApplyVoiceOverText: string;
      filterCloseText: string;
      filterClearText: string;
      filterSelectedAriaLabel: string;
      filterUnSelectedAriaLabel: string;
    };
    menu: {
      color: string;
      textColor: string;
      focusColor: string;
      focusTextColor: string;
      activeColor: string;
      activeTextColor: string;
    };
    searchBar: {
      searchPlaceholderText: string;
      searchNoResultsFoundText: string;
      searchResultsTotalText: string;
      resultsFoundText: string;
    };
  };
};
